import { DateTime } from '@ksyos/date-time';
import { Box, Card } from '@ksyos/design-system';
import { useEffect, useRef, useState } from 'react';
import type { StatusLabelStatus } from '@common/enums';
import type {
    AdminProcessesListAlertsState,
    IAdminProcess,
    IAdminProcessesListSearchState,
    NewOrOldAdminProcess,
    ShowModalType,
} from '@frontend/components/interfaces/adminProcessesList';
import { ManagerAuthorization } from '@common/enums';
import { AdminProcessesFields } from '@common/enums/adminProcessesFields';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { getSelectedLanguage, parseDateInput } from '@frontend/translate';
import {
    assertHasAuthorization,
    hasOneAuthorization,
    useAbortableFetch,
    useSearch,
    useStateWithPartialSet,
} from '@frontend/utils';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import { Loader } from '../../elements/common';
import { ListPager } from '../../elements/ListPager';
import { getCleanAdminProcessesSearchState, isAdminProcess } from './adminProcessesHelperFunctions';
import { AdminProcessesFooterButtons } from './adminProcessesList/AdminProcessesFooterButtons';
import { AdminProcessesListAlerts } from './adminProcessesList/AdminProcessesListAlerts';
import { AdminProcessesListSearchHeader } from './adminProcessesList/AdminProcessesListSearchHeader';
import { AdminProcessesModals } from './adminProcessesList/AdminProcessesModals';
import { AdminProcessesTable } from './adminProcessesList/AdminProcessesTable';

interface IGetAdminProcessesRequest {
    birthDate?: string;
    endCreationDate?: string;
    episodeCategoryId?: string;
    groupName?: string;
    identifierSocialSecurity?: string;
    includeInactives?: boolean;
    includeTotal?: boolean;
    name?: string;
    organization?: string;
    patientName?: string;
    practitioner?: string;
    processId?: string;
    searchText?: string;
    serviceId?: string;
    serviceType?: string;
    showAutomaticRightsOnly?: boolean;
    startCreationDate?: string;
    statusLabel?: StatusLabelStatus;
    txcsProcesId?: string;
}

export function AdminProcessesList() {
    const abortableFetch = useAbortableFetch();
    const canEdit = hasOneAuthorization(ManagerAuthorization.adminProcessEdit);
    const [alerts, setAlerts] = useStateWithPartialSet<AdminProcessesListAlertsState>({
        abortProcessCounter: 0,
        hasSearched: false,
    });
    const [showModal, setShowModal] = useState<ShowModalType>(null);
    const [searchState, setSearchState, pushSearchStateToHistory] = useSearch<IAdminProcessesListSearchState>(
        getCleanAdminProcessesSearchState()
    );
    const [processes, setProcesses] = useState<NewOrOldAdminProcess[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [totalProcesses, setTotalProcesses] = useState(0);
    const [succeedingConsultation, setSucceedingConsultation] = useState<IAdminProcess>();
    const [hasSearched, setHasSearched] = useState(false);
    const [hasMoreItems, setHasMoreItems] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const isInitialRender = useRef(true);
    const [updatedProcess, setUpdatedProcess] = useState<{ id: string } | null>(null);

    assertHasAuthorization(ManagerAuthorization.adminProcessRead);

    useEffect(searchEffect, [searchState, updatedProcess]);

    return (
        <Box marginTop={6}>
            <AdminProcessesListSearchHeader
                clearSearchState={clearSearchState}
                handleSearch={(state) => handleSearch({ ...state, page: 1 })}
                searchState={searchState}
            />
            <AdminProcessesListAlerts
                {...alerts}
                hasSearched={hasSearched}
                isSearching={isSearching}
                processesLength={processes.length}
            />
            {isSearching && <Loader horizontalCentered />}
            {processes.length > 0 && (
                <>
                    <Card>
                        <Card.Header title={getSelectedLanguage().common.searchResults} />
                        <AdminProcessesTable
                            processes={processes}
                            setProcesses={setProcesses}
                        />
                        <Card.Footer>
                            <ListPager
                                hasMoreItems={hasMoreItems}
                                isDisabled={isSearching}
                                isForwardOnly={searchState.includeTotal === false}
                                itemsPerPage={searchState.itemsPerPage}
                                onChangeItemsPerPage={(itemsPerPage) => {
                                    handleSearch({ itemsPerPage, page: 1 });
                                }}
                                onChangePage={handleChangePage}
                                page={currentPage}
                                total={totalProcesses}
                            />
                        </Card.Footer>
                    </Card>
                    {canEdit && (
                        <AdminProcessesFooterButtons
                            alerts={alerts}
                            loadSucceedingConsultation={loadSucceedingConsultation}
                            processes={processes}
                            setAlerts={setAlerts}
                            setShowModal={setShowModal}
                        />
                    )}
                </>
            )}
            {canEdit && (
                <AdminProcessesModals
                    alerts={alerts}
                    processes={processes}
                    setAlerts={setAlerts}
                    setProcesses={setProcesses}
                    setShowModal={setShowModal}
                    setUpdatedProcess={setUpdatedProcess}
                    showModal={showModal}
                    succeedingConsultation={succeedingConsultation}
                />
            )}
        </Box>
    );

    async function handleChangePage(page: number) {
        handleSearch({ page });
    }

    async function loadSucceedingConsultation(processId?: string) {
        if (processId) {
            const result = await abortableFetch.fetchJson(`/api/adminProcesses/${processId}`, 'GET');
            setSucceedingConsultation(result.json);
        }
    }

    function handleSearch(incomingSearchState: Partial<IAdminProcessesListSearchState>) {
        setUpdatedProcess(null);
        const newSearchState = { ...searchState, ...incomingSearchState };
        setSearchState(newSearchState);
        pushSearchStateToHistory(newSearchState);
    }

    function clearSearchState() {
        setUpdatedProcess(null);
        const cleanSearchState = { ...getCleanAdminProcessesSearchState(), itemsPerPage: searchState.itemsPerPage };
        setSearchState(cleanSearchState);
        pushSearchStateToHistory(cleanSearchState);
    }

    function searchEffect() {
        async function search() {
            setAlerts({ correctActionFeedback: undefined, incorrectActionFeedback: undefined });
            setIsSearching(true);

            const body = buildProcessPostBody();

            if (!body) {
                setProcesses([]);
                setTotalProcesses(0);
                setIsSearching(false);
            } else {
                const result = await abortableFetch.postJson(buildProcessUrl(), body);
                if (result.json && result.json.items) {
                    let items: NewOrOldAdminProcess[] = [];
                    if (result.json.total === 1) {
                        items = result.json.items.map((item: NewOrOldAdminProcess) => {
                            const newItem: NewOrOldAdminProcess = { ...item };
                            if (isAdminProcess(newItem)) {
                                newItem.checked = true;
                            }

                            return newItem;
                        });
                    } else {
                        items = result.json.items.map((item: NewOrOldAdminProcess) => {
                            const newItem: NewOrOldAdminProcess = { ...item };
                            if (isAdminProcess(newItem)) {
                                newItem.checked = updatedProcess ? item.id === updatedProcess.id : false;
                            }

                            return newItem;
                        });
                    }
                    setHasMoreItems(result.json.hasMoreItems);
                    setCurrentPage(searchState.page);
                    setProcesses(items);

                    if (searchState.includeTotal) {
                        if (searchState.page === 1) {
                            setTotalProcesses(result.json.total);
                        }
                    } else {
                        setTotalProcesses(result.json.items.length);
                    }

                    setIsSearching(false);
                }
            }

            setHasSearched(true);
        }

        if (isInitialRender.current) {
            isInitialRender.current = false;
        } else {
            search();
        }
    }

    function buildProcessUrl() {
        const urlBuilder = new UrlBuilder('/api/adminProcesses');

        return urlBuilder.add('page', searchState.page).add('limit', searchState.itemsPerPage).build();
    }

    function buildProcessPostBody() {
        const body: IGetAdminProcessesRequest = {};

        if (searchState.text) {
            const trimmedSearchText = searchState.text.trim();
            switch (searchState.field) {
                case AdminProcessesFields.processId:
                    if (
                        trimmedSearchText.match(/^[0-9]{1,10}$/) ||
                        trimmedSearchText.match(/^[a-zA-Z]{1,}[0-9]{1,10}$/)
                    ) {
                        body.processId = trimmedSearchText.replace(/^[a-zA-Z]*/, '').toUpperCase();
                    } else {
                        return false;
                    }
                    break;
                case AdminProcessesFields.identifierSocialSecurity:
                    if (trimmedSearchText.match(/^[0-9]{1,10}$/)) {
                        body.identifierSocialSecurity = trimmedSearchText;
                    } else {
                        return false;
                    }
                    break;
                case AdminProcessesFields.patientBirthdate: {
                    const birthDate = parseDateInput(trimmedSearchText, true);
                    if (birthDate === 'Invalid Date') {
                        return false;
                    } else {
                        body.birthDate = DateTime.from(birthDate).format('YYYY-MM-DD');
                    }
                    break;
                }
                case AdminProcessesFields.patientName:
                case AdminProcessesFields.txcsProcesId:
                case AdminProcessesFields.organization:
                case AdminProcessesFields.groupName:
                case AdminProcessesFields.practitioner:
                    body[searchState.field] = trimmedSearchText;
                    break;
                default: {
                    body.searchText = trimmedSearchText;
                    break;
                }
            }
        }

        if (searchState.serviceType) {
            body.serviceType = searchState.serviceType;
        }
        if (searchState.episodeCategoryId) {
            body.episodeCategoryId = searchState.episodeCategoryId;
        }
        if (searchState.serviceId) {
            body.serviceId = searchState.serviceId;
        }
        if (searchState.statusLabel && searchState.serviceType) {
            body.statusLabel = searchState.statusLabel;
        }
        if (searchState.startCreationDate) {
            body.startCreationDate = formatDateIntoString(searchState.startCreationDate) || undefined;
        }
        if (searchState.endCreationDate) {
            body.endCreationDate = formatDateIntoString(searchState.endCreationDate) || undefined;
        }
        if (searchState.showAutomaticRightsOnly) {
            body.showAutomaticRightsOnly = searchState.showAutomaticRightsOnly;
        }

        body.includeTotal = searchState.includeTotal && searchState.page === 1;
        body.includeInactives = false;

        return body;
    }
}
